import { useState } from "react";
import { makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { InputField } from "../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../components/input-button/InputButton";
import { BusinessWithdrawTarificationService } from "../../../../../services/BusinessWithdrawTarificationService";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import { validateFile } from "../../../../../common/Hooks";
import IcipWithdrawTarificationService from "../../../../../services/IcipWithdrawTarificationService";
import { Autocomplete } from "@material-ui/lab";
import AccountLimitService from "../../../../../services/AccountLimitService";

interface AddAccountLimitProps {
  pricingGroups: any[];
  onSubmitCallback: () => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 25,
    fontWeight: "bold",
  },
  modalBody: {
    width: 700,
    padding: 30,
    border: "none",
  },
  fieldNumb:{
    height:40
  },
  searchAuto:{ 
    //border: '1px outset lightgray',
      
  },

  antPickerDropdown: {
    zIndex: 100000,
  },
}));

export const AddAccountLimit = (
  props: AddAccountLimitProps
) => {
  const classes = useStyles();
  const intl = useIntl();
  const { formatMessage } = useIntl();
  const { pricingGroups, onSubmitCallback} =props;
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [succeed, setSucceed] = useState(false);

  const formik = useFormik({
    initialValues: {
      min_credit: "",
      max_credit: "",
      max_transaction_volume: "",
      max_monthly_volume: "",
      pricing_group_id: "",
    },
    validationSchema: Yup.object().shape({
      min_credit: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterMinCredit" })
      ),
      max_credit: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterMaxCredit" })
      ),
      max_transaction_volume: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterMaxTransactionVolume" })
      ),
      max_monthly_volume: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterMaxMonthlyVolume" })
      ),
      pricing_group_id: Yup.number().required(
        formatMessage({ id: "sharedScreen.pleaseEnterPricingGroupId" })
      ),
    }),
    onSubmit: async (
      values,
      { setSubmitting, resetForm }: FormikHelpers<any>
    ) => {
      setError(false);
      try {
        const addAccountLimitResult = await AccountLimitService.addAccountLimit(
          formik.values
        );
        if (
          addAccountLimitResult.isSuccess &&
          addAccountLimitResult.data.code === 100
        ) {
          resetForm({});
          setSucceed(true);
          setTimeout(() => {
            setSucceed(false);
          }, 2000);
          onSubmitCallback();
        } else {
          setError(true);
          if (addAccountLimitResult.data.code === 101) {
            setErrorMessage("Min credit must be lower than max credit");
          } else {
            if (addAccountLimitResult.data.code === 102) {
              setErrorMessage("Max transaction volume greated than max credit");
            } else {
              if (addAccountLimitResult.data.code === 103) {
                setErrorMessage(
                  "Similar account limit already exist"
                );
              } else {
                if (addAccountLimitResult.data.code === 104) {
                  setErrorMessage(
                    "There is overlap with another account limit"
                  );
                } 
              }
            }
          }
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  return (
    <>
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.addAccountLimit" />
              </Typography>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  {errorMessage === "" && (
                    <FormattedMessage id="sharedScreen.thereWasAProblemCreatingAccountLimit" />
                  )}
                  {errorMessage !== "" && <p>{errorMessage}</p>}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                className={classes.fieldNumb}
                value={formik.values.min_credit}
                type="number"
                fullWidth
                label={formatMessage({
                  id: "sharedScreen.accountLimitMinCredit",
                })}
                placeholder={formatMessage({
                  id: "sharedScreen.accountLimitMinCredit",
                })}
                name="min_credit"
                onChange={formik.handleChange}
                error={
                  formik.touched.min_credit && Boolean(formik.errors.min_credit)
                }
                helperText={
                  formik.touched.min_credit && formik.errors.min_credit
                }
              />

              <InputField
                className={classes.fieldNumb}
                value={formik.values.max_credit}
                type="number"
                fullWidth
                label={formatMessage({
                  id: "sharedScreen.accountLimitMaxCredit",
                })}
                placeholder={formatMessage({
                  id: "sharedScreen.accountLimitMaxCredit",
                })}
                name="max_credit"
                onChange={formik.handleChange}
                error={
                  formik.touched.max_credit && Boolean(formik.errors.max_credit)
                }
                helperText={
                  formik.touched.max_credit && formik.errors.max_credit
                }
              />

              <InputField
                className={classes.fieldNumb}
                value={formik.values.max_transaction_volume}
                type="number"
                fullWidth
                label={formatMessage({
                  id: "sharedScreen.accountLimitMaxTransactionVolume",
                })}
                placeholder={formatMessage({
                  id: "sharedScreen.accountLimitMaxTransactionVolume",
                })}
                name="max_transaction_volume"
                onChange={formik.handleChange}
                error={
                  formik.touched.max_transaction_volume &&
                  Boolean(formik.errors.max_transaction_volume)
                }
                helperText={
                  formik.touched.max_transaction_volume &&
                  formik.errors.max_transaction_volume
                }
              />

              <InputField
                className={classes.fieldNumb}
                value={formik.values.max_monthly_volume}
                variant="outlined"
                type="number"
                fullWidth
                label={formatMessage({
                  id: "sharedScreen.accountLimitMaxMonthlyVolume",
                })}
                placeholder={formatMessage({ id: "sharedScreen.accountLimitMaxMonthlyVolume" })}
                name="max_monthly_volume"
                onChange={formik.handleChange}
                error={formik.touched.max_monthly_volume && Boolean(formik.errors.max_monthly_volume)}
                helperText={formik.touched.dmax_monthly_volume && formik.errors.max_monthly_volume}
              />
            </Grid>

            <Grid item xs={12} sm={12} lg={12}>
            <Autocomplete
                id="pricingGroups"
                //value={formik.values.currency_id}
                options={pricingGroups}
                renderInput={param => (<TextField {...param} label={formatMessage({ id: "sharedScreen.pricingGroups" })}
                variant="outlined" size="small" />)}
                getOptionLabel= {Option => Option.code}
                //onChange={formik.handleChange}
               onChange ={(event, pricingGroup) => {
                {pricingGroup != null &&  formik.setFieldValue('pricing_group_id', pricingGroup.id)}
              }}
              />
            </Grid>

            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
              >
                <FormattedMessage id="sharedScreen.create" />
                {formik.isSubmitting && <CircularProgress size={30} />}
              </InputButton>
            </Grid>
            {succeed && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="success">
                  <FormattedMessage id="sharedScreen.accountLimitCreatedSuccessfully" />
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </Paper>
    </>
  );
};
